import React, { useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import { splitNumberDigits } from 'helpers/math'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import { useHistory } from 'react-router-dom'
import style from './StudentDynamicsReport.module.scss'

const TOTAL_METRICS = [
  { label: 'Проданные ак часы', prefix: 'sales' },
  { label: 'Отработано ак часов', prefix: 'consumed' },
  { label: 'Количество студентов', prefix: 'students' },
]

const StudentDynamicsReport = () => {
  const [catalog, setCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const { refWrapper, data, setData, isLoading, setIsLoading } =
    useTableFixedHeader()

  const { translate } = useLocalization({
    section: 'student_dynamics',
    subsection: 'all',
    dict: {
      filter: 'Filter',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
    },
  })

  const filterCnf = [
    {
      type: 'select-multi',
      label: 'Локация',
      nameState: 'location',
      placeholder: 'Локация',
      values:
        catalog.locations?.sort((a, b) => (a.name > b.name ? 1 : -1)) || [],
      option_value_key: 'id',
      option_label_key: 'name',
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from_1', 'date_until_1'],
      label: 'Период 2024-2025',
      isRequired: true,
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from_2', 'date_until_2'],
      label: 'Период 2025-2026',
      isRequired: true,
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from_3', 'date_until_3'],
      label: 'Период 2026-2027',
      isRequired: true,
    },
  ]

  const roundTo2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100

  useEffect(() => {
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/student_dynamics_report',
    })
      .then(({ data }) => {
        setCatalog({ locations: data.locations || [] })
        setData({ locations: [] })
        setIsLoading(false)
      })
      .catch(() => setIsLoading(false))
  }, [])

  const getMonthName = (monthNum) => {
    const months = [
      'Январь',
      'Февраль',
      'Март',
      'Апрель',
      'Май',
      'Июнь',
      'Июль',
      'Август',
      'Сентябрь',
      'Октябрь',
      'Ноябрь',
      'Декабрь',
    ]
    return months[monthNum - 1] || ''
  }

  useEffect(() => {
    if (
      !filter ||
      !filter.date_from_1 ||
      !filter.date_until_1 ||
      !filter.date_from_2 ||
      !filter.date_until_2 ||
      !filter.date_from_3 ||
      !filter.date_until_3
    ) {
      setData({ locations: [] })
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    const params = {
      date_from_1: filter.date_from_1,
      date_until_1: filter.date_until_1,
      date_from_2: filter.date_from_2,
      date_until_2: filter.date_until_2,
      date_from_3: filter.date_from_3,
      date_until_3: filter.date_until_3,
    }
    if (filter.location && filter.location.length > 0) {
      params.location = Array.isArray(filter.location)
        ? filter.location.join(',')
        : filter.location
    }

    superAxiosRequest({
      method: 'get',
      link: 'progress_report/student_dynamics_report',
      params,
    })
      .then(({ data }) => {
        const allMonths = new Set()
        const years = ['2024_2025', '2025_2026', '2026_2027']
        years.forEach((year) => {
          if (data[year]) {
            Object.keys(data[year]).forEach((locId) => {
              const locData = data[year][locId]
              Object.keys(locData).forEach((key) => {
                if (key !== 'location_id') allMonths.add(parseInt(key))
              })
            })
          }
        })
        const sortedMonths = Array.from(allMonths).sort((a, b) => a - b)

        const transformedData = (catalog.locations || [])
          .filter((location) => {
            if (filter.location && filter.location.length > 0)
              return filter.location.includes(location.id)
            return (
              data['2024_2025']?.[location.id] ||
              data['2025_2026']?.[location.id] ||
              data['2026_2027']?.[location.id]
            )
          })
          .map((location) => {
            const locationData = {}
            sortedMonths.forEach((month) => {
              const monthName = getMonthName(month)
              const data2024_2025 = data['2024_2025']?.[location.id]?.[month]
              if (data2024_2025) {
                locationData[`sales_${monthName}_2024_2025`] =
                  data2024_2025.sales_hours || 0
                locationData[`consumed_${monthName}_2024_2025`] =
                  data2024_2025.consumed || 0
                locationData[`students_${monthName}_2024_2025`] =
                  data2024_2025.students || 0
              }
              const data2025_2026 = data['2025_2026']?.[location.id]?.[month]
              if (data2025_2026) {
                locationData[`sales_${monthName}_2025_2026`] =
                  data2025_2026.sales_hours || 0
                locationData[`consumed_${monthName}_2025_2026`] =
                  data2025_2026.consumed || 0
                locationData[`students_${monthName}_2025_2026`] =
                  data2025_2026.students || 0
              }
              const data2026_2027 = data['2026_2027']?.[location.id]?.[month]
              if (data2026_2027) {
                const factData = data2026_2027.fact
                const planData = data2026_2027.plan
                locationData[`sales_${monthName}_2026_2027_plan`] =
                  planData?.sales_hours || 0
                locationData[`consumed_${monthName}_2026_2027_plan`] =
                  planData?.consumed || 0
                locationData[`students_${monthName}_2026_2027_plan`] =
                  planData?.students || 0
                locationData[`sales_${monthName}_2026_2027_fact`] =
                  factData?.sales_hours || 0
                locationData[`consumed_${monthName}_2026_2027_fact`] =
                  factData?.consumed || 0
                locationData[`students_${monthName}_2026_2027_fact`] =
                  factData?.students || 0
              }
            })
            return {
              id: location.id,
              name: location.name,
              data: locationData,
              months: sortedMonths.map((m) => ({
                num: m,
                name: getMonthName(m),
              })),
            }
          })
        setData({ locations: transformedData })
        setIsLoading(false)
      })
      .catch(() => {
        setData({ locations: [] })
        setIsLoading(false)
      })
  }, [
    filter?.date_from_1,
    filter?.date_until_1,
    filter?.date_from_2,
    filter?.date_until_2,
    filter?.date_from_3,
    filter?.date_until_3,
    filter?.location,
    catalog.locations,
  ])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/student-dynamics') updateFilter({})
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const calculateLocationTotals = (location) => {
    const totals = {
      sales_24_25: 0,
      consumed_24_25: 0,
      students_24_25: 0,
      sales_25_26: 0,
      consumed_25_26: 0,
      students_25_26: 0,
      sales_plan_26_27: 0,
      consumed_plan_26_27: 0,
      students_plan_26_27: 0,
      sales_fact_26_27: 0,
      consumed_fact_26_27: 0,
      students_fact_26_27: 0,
      sales_diff_26_27: 0,
      consumed_diff_26_27: 0,
      students_diff_26_27: 0,
    }
    location.months.forEach((month) => {
      const mName = month.name
      totals.sales_24_25 += location.data[`sales_${mName}_2024_2025`] || 0
      totals.consumed_24_25 += location.data[`consumed_${mName}_2024_2025`] || 0
      totals.students_24_25 += location.data[`students_${mName}_2024_2025`] || 0
      totals.sales_25_26 += location.data[`sales_${mName}_2025_2026`] || 0
      totals.consumed_25_26 += location.data[`consumed_${mName}_2025_2026`] || 0
      totals.students_25_26 += location.data[`students_${mName}_2025_2026`] || 0
      totals.sales_plan_26_27 +=
        location.data[`sales_${mName}_2026_2027_plan`] || 0
      totals.consumed_plan_26_27 +=
        location.data[`consumed_${mName}_2026_2027_plan`] || 0
      totals.students_plan_26_27 +=
        location.data[`students_${mName}_2026_2027_plan`] || 0
      totals.sales_fact_26_27 +=
        location.data[`sales_${mName}_2026_2027_fact`] || 0
      totals.consumed_fact_26_27 +=
        location.data[`consumed_${mName}_2026_2027_fact`] || 0
      totals.students_fact_26_27 +=
        location.data[`students_${mName}_2026_2027_fact`] || 0
    })
    // Разница = Факт - План
    totals.sales_diff_26_27 = roundTo2(
      totals.sales_fact_26_27 - totals.sales_plan_26_27,
    )
    totals.consumed_diff_26_27 = roundTo2(
      totals.consumed_fact_26_27 - totals.consumed_plan_26_27,
    )
    totals.students_diff_26_27 = roundTo2(
      totals.students_fact_26_27 - totals.students_plan_26_27,
    )
    return totals
  }

  const calculateGrandTotals = (locations) => {
    const totals = {
      sales_24_25: 0,
      consumed_24_25: 0,
      students_24_25: 0,
      sales_25_26: 0,
      consumed_25_26: 0,
      students_25_26: 0,
      sales_plan_26_27: 0,
      consumed_plan_26_27: 0,
      students_plan_26_27: 0,
      sales_fact_26_27: 0,
      consumed_fact_26_27: 0,
      students_fact_26_27: 0,
      sales_diff_26_27: 0,
      consumed_diff_26_27: 0,
      students_diff_26_27: 0,
    }
    locations.forEach((loc) => {
      const locTotals = calculateLocationTotals(loc)
      totals.sales_24_25 += locTotals.sales_24_25
      totals.consumed_24_25 += locTotals.consumed_24_25
      totals.students_24_25 += locTotals.students_24_25
      totals.sales_25_26 += locTotals.sales_25_26
      totals.consumed_25_26 += locTotals.consumed_25_26
      totals.students_25_26 += locTotals.students_25_26
      totals.sales_plan_26_27 += locTotals.sales_plan_26_27
      totals.consumed_plan_26_27 += locTotals.consumed_plan_26_27
      totals.students_plan_26_27 += locTotals.students_plan_26_27
      totals.sales_fact_26_27 += locTotals.sales_fact_26_27
      totals.consumed_fact_26_27 += locTotals.consumed_fact_26_27
      totals.students_fact_26_27 += locTotals.students_fact_26_27
    })
    // Разница = Факт - План
    totals.sales_diff_26_27 = roundTo2(
      totals.sales_fact_26_27 - totals.sales_plan_26_27,
    )
    totals.consumed_diff_26_27 = roundTo2(
      totals.consumed_fact_26_27 - totals.consumed_plan_26_27,
    )
    totals.students_diff_26_27 = roundTo2(
      totals.students_fact_26_27 - totals.students_plan_26_27,
    )
    return totals
  }

  const downloadExcel = (data) => {
    import('xlsx-js-style').then((XLSX) => {
      const wb = XLSX.utils.book_new()
      const wsData = []
      const merges = []
      const rowColors = []

      // Заголовки
      wsData.push([
        'Локация',
        'Период',
        'Тип',
        '2024-2025 факт',
        '2025-2026 факт',
        '2026-2027 План',
        '2026-2027 Факт',
        '2026-2027 Разница',
      ])

      let currentRow = 1

      data?.locations?.forEach((location) => {
        const months = location.months || []
        const startLocRow = currentRow

        // 1. Строки с месяцами
        months.forEach((month) => {
          const mName = month.name
          const startMonthRow = currentRow

          TOTAL_METRICS.forEach((metric) => {
            const p = metric.prefix
            const val24 = location.data[`${p}_${mName}_2024_2025`] || 0
            const val25 = location.data[`${p}_${mName}_2025_2026`] || 0
            const valPlan = location.data[`${p}_${mName}_2026_2027_plan`] || 0
            const valFact = location.data[`${p}_${mName}_2026_2027_fact`] || 0
            // Разница = Факт - План
            const valDiff = roundTo2(valFact - valPlan)

            wsData.push([
              '',
              '',
              metric.label,
              splitNumberDigits(val24),
              splitNumberDigits(val25),
              splitNumberDigits(valPlan),
              splitNumberDigits(valFact),
              splitNumberDigits(valDiff),
            ])
            currentRow++
          })

          merges.push({
            s: { r: startMonthRow, c: 1 },
            e: { r: currentRow - 1, c: 1 },
          })
          wsData[startMonthRow][1] = mName
        })

        const endMonthRow = currentRow - 1

        merges.push({
          s: { r: startLocRow, c: 0 },
          e: { r: endMonthRow, c: 0 },
        })
        wsData[startLocRow][0] = location.name

        // 2. Строки "Итого по локации"
        const locTotals = calculateLocationTotals(location)
        const startTotalRow = currentRow

        TOTAL_METRICS.forEach((metric, idx) => {
          wsData.push([
            idx === 0 ? 'Итого по локации' : '',
            '',
            metric.label,
            splitNumberDigits(locTotals[`${metric.prefix}_24_25`]),
            splitNumberDigits(locTotals[`${metric.prefix}_25_26`]),
            splitNumberDigits(locTotals[`${metric.prefix}_plan_26_27`]),
            splitNumberDigits(locTotals[`${metric.prefix}_fact_26_27`]),
            splitNumberDigits(locTotals[`${metric.prefix}_diff_26_27`]),
          ])
          rowColors.push({
            rowIndex: currentRow,
            color: '5a8dee',
            isTotal: true,
          })
          currentRow++
        })

        merges.push({
          s: { r: startTotalRow, c: 0 },
          e: { r: currentRow - 1, c: 0 },
        })
      })

      // 3. Общие итого
      if (data?.locations?.length > 0) {
        const grandTotals = calculateGrandTotals(data.locations)
        const startGrandRow = currentRow

        TOTAL_METRICS.forEach((metric, idx) => {
          wsData.push([
            idx === 0 ? 'Общие итого' : '',
            '',
            metric.label,
            splitNumberDigits(grandTotals[`${metric.prefix}_24_25`]),
            splitNumberDigits(grandTotals[`${metric.prefix}_25_26`]),
            splitNumberDigits(grandTotals[`${metric.prefix}_plan_26_27`]),
            splitNumberDigits(grandTotals[`${metric.prefix}_fact_26_27`]),
            splitNumberDigits(grandTotals[`${metric.prefix}_diff_26_27`]),
          ])
          rowColors.push({
            rowIndex: currentRow,
            color: '5a8dee',
            isTotal: true,
          })
          currentRow++
        })

        merges.push({
          s: { r: startGrandRow, c: 0 },
          e: { r: currentRow - 1, c: 0 },
        })
      }

      const ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!merges'] = merges

      ws['!cols'] = [
        { wch: 20 },
        { wch: 12 },
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ]

      const borderStyle = {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      }

      const range = XLSX.utils.decode_range(ws['!ref'])
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
          if (!ws[cellAddress]) ws[cellAddress] = { t: 's', v: '' }

          let style = {
            font: { sz: 10, color: { rgb: '000000' } },
            alignment: {
              horizontal: 'center',
              vertical: 'center',
              wrapText: true,
            },
            border: borderStyle,
          }

          if (R === 0) {
            style.font.bold = true
            style.fill = { fgColor: { rgb: 'E8E8E8' } }
          }

          const colorInfo = rowColors.find((rc) => rc.rowIndex === R)
          if (colorInfo) {
            style.fill = { fgColor: { rgb: colorInfo.color } }
            style.font = { sz: 10, bold: true, color: { rgb: 'FFFFFF' } }
          }

          if (C === 7 && colorInfo?.isTotal) {
            const cellValue = ws[cellAddress]?.v
            const numericValue =
              typeof cellValue === 'string'
                ? parseFloat(cellValue.replace(/\s/g, '').replace(',', '.'))
                : cellValue

            if (numericValue < 0) {
              style.font = { sz: 13, bold: true, color: { rgb: 'FF0000' } }
            }
          }

          ws[cellAddress].s = style
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Динамика студентов')
      XLSX.writeFile(wb, 'Динамика_студентов.xlsx')
    })
  }

  const locations = data?.locations || []
  const isFilterEmpty = !filterTextValues.length
  const hasRequiredFilters =
    filter &&
    filter.date_from_1 &&
    filter.date_until_1 &&
    filter.date_from_2 &&
    filter.date_until_2 &&
    filter.date_from_3 &&
    filter.date_until_3

  return (
    <>
      <ContentHeader title='План по студентам' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder='Search...'
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter || {}}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : !hasRequiredFilters ? (
        <h1 className={st.noData}>Выберите периоды для отображения данных</h1>
      ) : data?.locations && data.locations.length > 0 ? (
        <>
          <div style={{ padding: '10px', marginBottom: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>
          <div
            style={{ padding: '10px', overflowX: 'auto' }}
            className={st.tableWrapper}
            ref={refWrapper}
          >
            <table
              className={st.table}
              style={{ minWidth: '100%', borderCollapse: 'collapse' }}
            >
              <thead>
                <tr>
                  {[
                    'Локация',
                    'Период',
                    'Тип',
                    '2024-2025 факт',
                    '2025-2026 факт',
                    '2026-2027 План',
                    '2026-2027 Факт',
                    '2026-2027 Разница',
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: '1px solid #ddd',
                        padding: '8px',
                        background: '#f0f0f0',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => {
                  const months = location.months || []
                  const locationRowSpan = months.length * 3 + 3

                  return (
                    <React.Fragment key={location.id}>
                      {months.map((month, monthIdx) => {
                        const mName = month.name
                        return (
                          <React.Fragment key={month.num}>
                            {TOTAL_METRICS.map((row, rowIdx) => {
                              const p = row.prefix
                              const v24 =
                                location.data[`${p}_${mName}_2024_2025`] || 0
                              const v25 =
                                location.data[`${p}_${mName}_2025_2026`] || 0
                              const vPlan =
                                location.data[`${p}_${mName}_2026_2027_plan`] ||
                                0
                              const vFact =
                                location.data[`${p}_${mName}_2026_2027_fact`] ||
                                0
                              // Разница = Факт - План
                              const vDiff = roundTo2(vFact - vPlan)

                              return (
                                <tr key={`${month.num}-${rowIdx}`}>
                                  {monthIdx === 0 && rowIdx === 0 && (
                                    <td
                                      rowSpan={locationRowSpan}
                                      style={{
                                        border: '1px solid #ddd',
                                        padding: '8px',
                                        fontWeight: '500',
                                        verticalAlign: 'middle',
                                        textAlign: 'center',
                                      }}
                                    >
                                      {location.name}
                                    </td>
                                  )}
                                  {rowIdx === 0 && (
                                    <td
                                      rowSpan={3}
                                      style={{
                                        border: '1px solid #ddd',
                                        padding: '8px',
                                        verticalAlign: 'middle',
                                        textAlign: 'center',
                                      }}
                                    >
                                      {mName}
                                    </td>
                                  )}
                                  <td
                                    style={{
                                      border: '1px solid #ddd',
                                      padding: '8px',
                                    }}
                                  >
                                    {row.label}
                                  </td>
                                  <td
                                    style={{
                                      border: '1px solid #ddd',
                                      padding: '8px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {splitNumberDigits(v24)}
                                  </td>
                                  <td
                                    style={{
                                      border: '1px solid #ddd',
                                      padding: '8px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {splitNumberDigits(v25)}
                                  </td>
                                  <td
                                    style={{
                                      border: '1px solid #ddd',
                                      padding: '8px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {splitNumberDigits(vPlan)}
                                  </td>
                                  <td
                                    style={{
                                      border: '1px solid #ddd',
                                      padding: '8px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {splitNumberDigits(vFact)}
                                  </td>
                                  <td
                                    style={{
                                      border: '1px solid #ddd',
                                      padding: '8px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {splitNumberDigits(vDiff)}
                                  </td>
                                </tr>
                              )
                            })}
                          </React.Fragment>
                        )
                      })}

                      {(() => {
                        const locTotals = calculateLocationTotals(location)
                        return TOTAL_METRICS.map((metric, idx) => (
                          <tr
                            key={`loc-total-${metric.prefix}`}
                            style={{
                              backgroundColor: '#5a8dee',
                              color: 'white',
                            }}
                          >
                            {idx === 0 && (
                              <td
                                rowSpan={3}
                                style={{
                                  border: '1px solid #ddd',
                                  padding: '8px',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                }}
                              >
                                Итого по локации
                              </td>
                            )}
                            <td
                              colSpan={1}
                              style={{
                                border: '1px solid #ddd',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                              }}
                            >
                              {metric.label}
                            </td>
                            <td
                              style={{
                                border: '1px solid #ddd',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                              }}
                            >
                              {splitNumberDigits(
                                locTotals[`${metric.prefix}_24_25`],
                              )}
                            </td>
                            <td
                              style={{
                                border: '1px solid #ddd',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                              }}
                            >
                              {splitNumberDigits(
                                locTotals[`${metric.prefix}_25_26`],
                              )}
                            </td>
                            <td
                              style={{
                                border: '1px solid #ddd',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                              }}
                            >
                              {splitNumberDigits(
                                locTotals[`${metric.prefix}_plan_26_27`],
                              )}
                            </td>
                            <td
                              style={{
                                border: '1px solid #ddd',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                              }}
                            >
                              {splitNumberDigits(
                                locTotals[`${metric.prefix}_fact_26_27`],
                              )}
                            </td>
                            <td
                              style={{
                                border: '1px solid #ddd',
                                padding: '8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                ...(locTotals[`${metric.prefix}_diff_26_27`] < 0
                                  ? { color: 'red', fontSize: '15px' }
                                  : { color: 'white' }),
                              }}
                            >
                              {splitNumberDigits(
                                locTotals[`${metric.prefix}_diff_26_27`],
                              )}
                            </td>
                          </tr>
                        ))
                      })()}
                    </React.Fragment>
                  )
                })}

                {locations.length > 0 &&
                  (() => {
                    const grandTotals = calculateGrandTotals(locations)
                    return TOTAL_METRICS.map((metric, idx) => (
                      <tr
                        key={`grand-total-${metric.prefix}`}
                        style={{ backgroundColor: '#5a8dee', color: 'white' }}
                      >
                        {idx === 0 && (
                          <td
                            rowSpan={3}
                            style={{
                              border: '1px solid #ddd',
                              padding: '8px',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                            }}
                          >
                            Общие итого
                          </td>
                        )}
                        <td
                          colSpan={2}
                          style={{
                            border: '1px solid #ddd',
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                          }}
                        >
                          {metric.label}
                        </td>
                        <td
                          style={{
                            border: '1px solid #ddd',
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                          }}
                        >
                          {splitNumberDigits(
                            grandTotals[`${metric.prefix}_24_25`],
                          )}
                        </td>
                        <td
                          style={{
                            border: '1px solid #ddd',
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                          }}
                        >
                          {splitNumberDigits(
                            grandTotals[`${metric.prefix}_25_26`],
                          )}
                        </td>
                        <td
                          style={{
                            border: '1px solid #ddd',
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                          }}
                        >
                          {splitNumberDigits(
                            grandTotals[`${metric.prefix}_plan_26_27`],
                          )}
                        </td>
                        <td
                          style={{
                            border: '1px solid #ddd',
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                          }}
                        >
                          {splitNumberDigits(
                            grandTotals[`${metric.prefix}_fact_26_27`],
                          )}
                        </td>
                        <td
                          style={{
                            border: '1px solid #ddd',
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            ...(grandTotals[`${metric.prefix}_diff_26_27`] < 0
                              ? { color: 'red', fontSize: '15px' }
                              : { color: 'white' }),
                          }}
                        >
                          {splitNumberDigits(
                            grandTotals[`${metric.prefix}_diff_26_27`],
                          )}
                        </td>
                      </tr>
                    ))
                  })()}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для выбранных фильтров</h1>
      )}
    </>
  )
}

export default observer(StudentDynamicsReport)
